import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirstResults } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info, Calendar, Building2, GraduationCap } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import '../styles/FirstResult.css';

const FirstResult = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFirstResults();
  }, [user, navigate]);

  const fetchFirstResults = async () => {
    try {
      setIsLoading(true);
      const firstResults = await getFirstResults();
      setResults(Array.isArray(firstResults) ? firstResults : []);
    } catch (err) {
      console.error('1次配属結果の取得に失敗しました:', err);
      setError('1次配属結果の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => (a.term_id || 0) - (b.term_id || 0));
  }, [results]);

  if (!user) return null;
  if (isLoading) {
    return <LoadingSpinner text="1次配属結果を読み込んでいます..." />;
  }
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <div className="text-destructive text-xl font-bold">{error}</div>
      </div>
    );
  }

  return (
    <div className="first-result-container">
      <Card className="dashboard-card full-width hover-effect">
        <CardHeader className="card-header">
          <div className="header-icon-wrapper">
            <GraduationCap className="header-icon" size={32} />
          </div>
          <CardTitle className="card-title">1次配属結果</CardTitle>
          <CardDescription className="card-description">
            1次抽選の結果をご確認ください
          </CardDescription>
        </CardHeader>
        <CardContent className="card-content-custom">
          {sortedResults.length === 0 ? (
            <div className="empty-state fade-in">
              <div className="empty-icon-circle">
                <Info size={32} />
              </div>
              <h3>結果待ち</h3>
              <p>現在、配属結果は登録されていません。</p>
            </div>
          ) : (
            <div className="result-grid fade-in-stagger">
              {sortedResults.map((result, index) => {
                const uniqueKey = result.assignment_id ?? `${result.term_id}-${result.institution_name}-${result.department_name}`;
                return (
                  <div key={uniqueKey} className="result-card" style={{ animationDelay: `${index * 100}ms` }}>
                    <div className="term-section">
                      <div className="term-label">
                        <Calendar size={14} className="term-icon" />
                        <span>TERM</span>
                      </div>
                      <div className="term-number">{result.term_id}</div>
                    </div>

                    <div className="assignment-details">
                      <div className="detail-row">
                        <Building2 size={16} className="detail-icon" />
                        <span className="institution-name">{result.institution_name}</span>
                      </div>
                      {result.department_name && (
                        <div className="detail-row department-row">
                          <span className="department-badge">{result.department_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FirstResult;
